import type React from 'react';
import { FormField, fieldDescribedBy } from '~/components/form/FormField';

export interface TextareaProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Fires when focus leaves the textarea — used to validate a manual compose file in place. */
  onBlur?: () => void;
  rows?: number;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: React.ReactNode;
  /** Replaces helperText. */
  error?: string;
  labelAction?: React.ReactNode;
  /** Native limit; also shows a `count/limit` counter under the field. */
  maxLength?: number;
  /** An outside element that also describes this field (its id). */
  describedBy?: string;
  /** Monospace, no spellcheck — compose files, keys, JSON. */
  code?: boolean;
  testId: string;
  ariaLabel?: string;
}

/** A multi-line text field: the `FormField` frame around a `field-control-multiline` textarea. */
export function Textarea({
  id,
  label,
  value,
  onChange,
  onBlur,
  rows = 4,
  required,
  readOnly,
  disabled,
  placeholder,
  helperText,
  error,
  labelAction,
  maxLength,
  describedBy,
  code,
  testId,
  ariaLabel,
}: TextareaProps) {
  return (
    <FormField
      id={id}
      label={label}
      required={required}
      labelAction={labelAction}
      helperText={helperText}
      error={error}
      counter={maxLength === undefined ? undefined : { count: value.length, limit: maxLength }}
    >
      <textarea
        id={id}
        data-testid={testId}
        aria-label={ariaLabel}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        required={required}
        readOnly={readOnly}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        spellCheck={code ? false : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={fieldDescribedBy(
          id,
          helperText,
          error,
          describedBy,
          maxLength !== undefined,
        )}
        className={`field-control-multiline read-only:text-gray-500 dark:read-only:text-gray-400 ${code ? 'font-mono' : ''}`}
      />
    </FormField>
  );
}
